/**
 * Booking helper utilities
 * Extracted to reduce complexity in main component
 */

import { getBookings, type Booking, type Ambulance } from '../../../services/api';
import { ACTIVE_BOOKING_STATUSES, type BookingStatus } from '../../../constants/booking';

/**
 * Extract booking ID from ambulance spec
 */
export const extractBookingIdFromAmbulance = (ambulance: Ambulance): string | null => {
  const activeBooking = (ambulance.spec as any)?.activeBooking;
  if (!activeBooking) return null;

  return (
    activeBooking._id ||
    activeBooking.bookingId ||
    activeBooking.id ||
    null
  );
};

/**
 * Find booking ID from active bookings array
 */
export const findBookingIdFromActiveBookings = (
  ambulanceId: string,
  activeBookings: Booking[]
): string | null => {
  const foundBooking = activeBookings.find((b) => b.spec.ambulanceId === ambulanceId);
  return foundBooking?._id || null;
};

/**
 * Query API for active booking
 */
export const queryActiveBookingFromAPI = async (
  ambulanceId: string
): Promise<Booking | null> => {
  try {
    const bookingsResponse = await getBookings(
      1,
      100,
      undefined,
      undefined,
      ambulanceId,
      undefined,
      undefined
    );

    const bookings = bookingsResponse.data.data || [];
    const activeBooking = bookings.find(
      (b: Booking) =>
        b.spec.ambulanceId === ambulanceId &&
        ACTIVE_BOOKING_STATUSES.includes(b.spec.status as BookingStatus)
    );

    return activeBooking || null;
  } catch (err) {
    console.error('[Tracking] Error querying bookings API:', err);
    return null;
  }
};

/**
 * Get booking ID using multiple fallback strategies
 */
export const getBookingId = async (
  ambulance: Ambulance,
  activeBookings: Booking[]
): Promise<string | null> => {
  // Strategy 1: Extract from ambulance spec
  let bookingId = extractBookingIdFromAmbulance(ambulance);
  if (bookingId) return bookingId;

  // Strategy 2: Find from active bookings array
  bookingId = findBookingIdFromActiveBookings(ambulance._id, activeBookings);
  if (bookingId) return bookingId;

  // Strategy 3: Query API
  const activeBooking = await queryActiveBookingFromAPI(ambulance._id);
  return activeBooking?._id || null;
};

